import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, ChevronLeft, ChevronRight } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { useCreateListing } from "@/hooks/useAds";
import { useRegaCompliance } from "@/hooks/useRega";
import { useRegaAutoCreate } from "@/hooks/useRegaAutoCreate";
import { buildRegaDisplayData } from "@/lib/rega-mappers";
import {
  companiesRepository,
  profilesRepository,
  regaRepository,
} from "@/repositories";
import { useProperty } from "@/hooks/useProperties";
import { useUnit } from "@/hooks/useUnits";
import { NafathVerificationModal } from "@/components/nafath/NafathVerificationModal";
import { useNafathIdentity } from "@/hooks/useNafathIdentity";
import { useCompanySettings } from "@/hooks/useSettings";
import { ListerStep } from "./ListerStep";
import { AdvertisementStep } from "./AdvertisementStep";
import { ReviewStep } from "./ReviewStep";
import { applyListerChange, shouldPersistUnifiedNumber, deriveListingPricing } from "./listingWizard.utils";
import type { ListerData, AdvertisementData, UnitInitialData } from "./types";
import * as Sentry from "@sentry/react";
import type { RegaValidateResponse } from "@/types/rega";

interface AddAdWizardDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialUnitId?: string;
  initialPropertyId?: string;
  initialPrice?: number;
  initialImages?: string[];
  /** Receives the created listing id so callers can link it back (e.g. a task). */
  onSuccess?: (listingId?: string) => void;
}

const TOTAL_STEPS = 3;

export function AddAdWizardDialog({
  open,
  onOpenChange,
  initialUnitId,
  initialPropertyId,
  initialImages,
  onSuccess,
}: AddAdWizardDialogProps) {
  const { t, language } = useLanguage();
  const { user, profile, companyId, refreshProfile } = useAuth();
  const createListing = useCreateListing();
  const regaCompliance = useRegaCompliance();
  const regaAutoCreate = useRegaAutoCreate();
  const { nafathIdentity } = useNafathIdentity();
  const { data: companySettings } = useCompanySettings();

  const storedUnifiedNumber = companySettings?.unified_number ?? null;

  const [currentStep, setCurrentStep] = useState(1);
  const [isIAMVerified, setIsIAMVerified] = useState(false);
  const [nafathModalOpen, setNafathModalOpen] = useState(false);
  const [regaData, setRegaData] = useState<RegaValidateResponse | null>(null);

  // Step 1 - Lister Data
  const [listerData, setListerData] = useState<ListerData>({
    idType: "individual",
    idNumber: "",
    licenseNumber: "",
    licenseValidated: false,
    propertyId: initialPropertyId || "",
    unitId: initialUnitId || "",
  });

  // Fetch property and unit data
  const { data: propertyData } = useProperty(listerData.propertyId || undefined);
  const { data: unitData } = useUnit(listerData.unitId || undefined);

  // Step 2 - Advertisement Data
  const [advertisementData, setAdvertisementData] = useState<AdvertisementData>({
    title: "",
    description: "",
    images: [],
    existingImages: initialImages || [],
  });

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setCurrentStep(1);
      setIsIAMVerified(!!profile?.nafath_verified);
      setRegaData(null);
      setListerData({
        idType: "individual",
        idNumber: "",
        licenseNumber: "",
        licenseValidated: false,
        propertyId: initialPropertyId || "",
        unitId: initialUnitId || "",
      });
      setAdvertisementData({
        title: "",
        description: "",
        images: [],
        existingImages: initialImages || [],
      });
    }
  }, [open, initialPropertyId, initialUnitId, initialImages]);

  const handleListerChange = (data: Partial<ListerData>) => {
    setListerData(prev => applyListerChange(prev, data, storedUnifiedNumber));
  };

  const handleAdvertisementChange = (data: Partial<AdvertisementData>) => {
    setAdvertisementData(prev => ({ ...prev, ...data }));
  };

  const handleRegaValidated = async (data: RegaValidateResponse) => {
    setRegaData(data);
    setListerData(prev => ({ ...prev, licenseValidated: true }));

    // Persist the corporate unified number back to the company record so it
    // pre-fills automatically next time (fire-and-forget — never block the wizard).
    if (shouldPersistUnifiedNumber(listerData.idType, listerData.idNumber, storedUnifiedNumber, companyId)) {
      companiesRepository
        .setUnifiedNumber(companyId, listerData.idNumber)
        .then(({ error }) => {
          if (error) {
            toast.error(t("تعذّر حفظ الرقم الموحد للمنشأة", "Failed to save unified number"));
            Sentry.captureException(error, { tags: { context: "persist_unified_number" } });
          }
        });
    }

    // If the user already picked both a property and a unit, there is nothing
    // to pre-select — keep their choice as-is, do NOT create anything.
    if (listerData.propertyId && listerData.unitId) {
      return;
    }

    // In the embedded flow the property is fixed via initialPropertyId.
    // No deed lookup needed — just leave the dropdowns as they are.
    if (initialPropertyId) {
      return;
    }

    // Standalone wizard (no pre-selected property): do a deed lookup to
    // pre-select a matching property + unit. NEVER create here.
    try {
      const match = await regaAutoCreate.matchByDeed(data);
      if (match) {
        setListerData(prev => ({
          ...prev,
          // Only pre-select if the user hasn't already typed something
          propertyId: prev.propertyId || match.propertyId,
          unitId: prev.unitId || (match.unitId ?? ""),
          regaResolved: {
            propertyId: match.propertyId,
            // unitId required by the type — use the matched one or empty
            unitId: match.unitId ?? "",
            propertyName: match.propertyName,
            wasCreated: false,
          },
        }));
      }
      // No match → leave dropdowns empty for the user to pick.
      // Property/unit will be created (if still empty) at submit time.
    } catch (error) {
      // Non-fatal — deed lookup failed; the user can still pick manually.
      toast.error(t("تعذر البحث عن العقار برقم الصك، يرجى الاختيار يدوياً", "Could not search by deed number. Please select manually."));
    }
  };

  const handleIAMVerify = () => {
    setNafathModalOpen(true);
  };


  // Step validation
  const isStep1Valid = () => {
    const baseValid = !!(
      listerData.idType &&
      listerData.idNumber &&
      listerData.licenseNumber &&
      listerData.licenseValidated
    );
    if (!baseValid) return false;

    // If the user has selected both property and unit, we're good.
    if (listerData.propertyId && listerData.unitId) return true;

    // If no property is selected but REGA data is available, creation will
    // happen at publish time — allow the user to proceed.
    if (regaData) return true;

    // Otherwise the user must pick a property+unit.
    return false;
  };

  const isStep2Valid = () => {
    const hasImages = advertisementData.images.length > 0 || advertisementData.existingImages.length > 0;
    return (
      advertisementData.title &&
      advertisementData.description &&
      hasImages
    );
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1: return isStep1Valid();
      case 2: return isStep2Valid();
      default: return true;
    }
  };

  const handleNext = () => {
    if (currentStep < TOTAL_STEPS && canProceed()) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handlePublish = async () => {
    try {
      // Listing type + derived total/annual price from REGA — never the per-m²
      // rate parcel-priced types (land, school) are fetched with. Pure logic in
      // listingWizard.utils (unit-tested there).
      const { listingType, price } = deriveListingPricing(regaData);

      let propertyId = listerData.propertyId;
      let unitId = listerData.unitId;

      // Deferred creation path: if the user submitted without selecting a
      // property (and REGA data is available), create property + unit now.
      // This is the ONLY legitimate create path — it never runs at validation.
      if ((!propertyId || !unitId) && regaData) {
        try {
          const created = await regaAutoCreate.createFromRega(regaData);
          propertyId = created.propertyId;
          unitId = created.unitId;
        } catch (createError) {
          console.error("Failed to create property from REGA data:", createError);
          toast.error(t("تعذر إنشاء العقار تلقائياً من بيانات الرخصة", "Failed to create property from REGA data automatically."));
          return;
        }
      }

      if (!propertyId || !unitId) {
        toast.error(t("يرجى تحديد العقار والوحدة قبل النشر", "Please select a property and unit before publishing."));
        return;
      }

      // Build REGA display snapshot for the public page
      const regaDisplayData = regaData ? buildRegaDisplayData(regaData) : undefined;

      const result = await createListing.mutateAsync({
        unit_id: unitId,
        property_id: propertyId,
        title: advertisementData.title,
        description: advertisementData.description,
        listing_type: listingType,
        price,
        published_at: new Date().toISOString(),
        images: advertisementData.images,
        existingImages: advertisementData.existingImages,
        rega_display_data: regaDisplayData ?? null,
        ad_license_number: listerData.licenseNumber || null,
      });

      // Link REGA license to newly created listing, then fire DisplayAd compliance
      if (listerData.licenseNumber && result?.id) {
        const { error: linkError } = await regaRepository.linkLicenseToListing(
          listerData.licenseNumber,
          result.id,
          companyId || null,
        );

        if (linkError) {
          console.warn("Failed to link REGA license:", linkError.message);
          toast.warning(t("تعذر ربط رخصة هيئة العقار", "Failed to link REGA license"));
        } else {
          const skipCompliance = import.meta.env.VITE_SKIP_REGA_COMPLIANCE === 'true';
          if (!skipCompliance) {
            regaCompliance.mutate({
              adLicenseNumber: listerData.licenseNumber,
              operationType: "DisplayAd",
              listingId: result.id,
            });
          } else {
            console.log('[REGA] Compliance skipped (VITE_SKIP_REGA_COMPLIANCE=true)');
          }
        }
      }

      onOpenChange(false);
      onSuccess?.(result?.id);
    } catch (error) {
      console.error("Error publishing listing:", error);
    }
  };

  const stepTitles = [
    t("المُعلن", "Lister"),
    t("الإعلان", "Advertisement"),
    t("المراجعة", "Review"),
  ];

  // Build unit data for review step
  const reviewUnitData: UnitInitialData | null = unitData ? {
    id: unitData.id,
    unit_number: unitData.unit_number,
    unit_type: unitData.unit_type,
    area: unitData.area,
    bedrooms: unitData.bedrooms,
    bathrooms: unitData.bathrooms,
    rent_amount: unitData.rent_amount,
    annual_rent: unitData.annual_rent,
    sale_price: unitData.sale_price,
    property_id: unitData.property_id,
    images: unitData.images as string[] | undefined,
    property: propertyData ? {
      id: propertyData.id,
      name: propertyData.name,
      name_en: propertyData.name_en || undefined,
      city: propertyData.city || undefined,
      district: propertyData.district || undefined,
      address: propertyData.address || undefined,
      latitude: propertyData.latitude || undefined,
      longitude: propertyData.longitude || undefined,
      postal_code: propertyData.postal_code || undefined,
      property_type: propertyData.property_type || undefined,
      built_year: propertyData.built_year || undefined,
      floors_count: propertyData.floors_count || undefined,
    } : undefined,
  } : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">
            {t("إضافة إعلان", "Add Listing")}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {t("معالج خطوات لإنشاء إعلان عقاري جديد ونشره على المنصة", "Step-by-step wizard to create and publish a new property listing")}
          </DialogDescription>
        </DialogHeader>

        {/* Step Indicator */}
        <div className="flex items-center justify-center gap-2 py-4 border-b">
          {stepTitles.map((title, index) => {
            const stepNum = index + 1;
            const isActive = stepNum === currentStep;
            const isCompleted = stepNum < currentStep;

            return (
              <div key={stepNum} className="flex items-center gap-2">
                <div
                  className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-primary text-primary-foreground'
                      : isCompleted
                        ? 'bg-primary/20 text-primary'
                        : 'bg-muted text-muted-foreground'
                  }`}
                >
                  {stepNum}
                </div>
                <span className={`text-sm ${isActive ? 'font-medium' : 'text-muted-foreground'}`}>
                  {title}
                </span>
                {index < stepTitles.length - 1 && (
                  <div className={`w-8 h-0.5 mx-2 ${isCompleted ? 'bg-primary' : 'bg-muted'}`} />
                )}
              </div>
            );
          })}
        </div>

        {/* Step Content */}
        <div className="flex-1 overflow-y-auto py-4 px-1">
          {currentStep === 1 && (
            <ListerStep
              data={listerData}
              onChange={handleListerChange}
              regaData={regaData}
              onRegaValidated={handleRegaValidated}
              isIAMVerified={isIAMVerified}
              onIAMVerify={handleIAMVerify}
              isPrefilledProperty={!!initialPropertyId}
              isPrefilledUnit={!!initialUnitId}
              nafathIdentity={nafathIdentity}
              isResolvingProperty={regaAutoCreate.isMatching}
            />
          )}
          {currentStep === 2 && (
            <AdvertisementStep
              data={advertisementData}
              onChange={handleAdvertisementChange}
              propertyImages={propertyData?.images as string[] | null}
              unitImages={unitData?.images as string[] | null}
            />
          )}
          {currentStep === 3 && (
            <ReviewStep
              listerData={listerData}
              advertisementData={advertisementData}
              regaData={regaData}
              unitData={reviewUnitData}
              propertyData={reviewUnitData?.property}
              propertyId={listerData.propertyId || reviewUnitData?.property?.id}
            />
          )}
        </div>

        {/* Footer Navigation */}
        <div className="flex items-center justify-between border-t pt-4">
          <Button
            variant="outline"
            onClick={handleBack}
            disabled={currentStep === 1}
            className="gap-2"
          >
            <ChevronRight className="h-4 w-4 rtl:rotate-180" />
            {t("السابق", "Back")}
          </Button>

          {currentStep < TOTAL_STEPS ? (
            <Button
              onClick={handleNext}
              disabled={!canProceed()}
              className="gap-2"
            >
              {t("التالي", "Next")}
              <ChevronLeft className="h-4 w-4 rtl:rotate-180" />
            </Button>
          ) : (
            <Button
              onClick={handlePublish}
              disabled={createListing.isPending}
              className="gap-2"
            >
              {createListing.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              {t("نشر", "Publish")}
            </Button>
          )}
        </div>
      </DialogContent>

      <NafathVerificationModal
        open={nafathModalOpen}
        onOpenChange={setNafathModalOpen}
        nationalId={profile?.national_id || listerData.idNumber || undefined}
        userId={user?.id}
        companyId={companyId || undefined}
        onVerified={(_identity, _nationalId) => {
          setIsIAMVerified(true);
          // Persist to DB so the verified flag survives reopen/reload.
          // Local state is already set above; this is best-effort — the user
          // can proceed immediately while the write completes in the background.
          if (user?.id) {
            profilesRepository
              .markSelfNafathVerified(user.id)
              .then(({ error }) => {
                if (error) {
                  Sentry.captureException(error, {
                    tags: { context: "nafath_persist_wizard" },
                  });
                } else {
                  void refreshProfile();
                }
              });
          }
        }}
      />
    </Dialog>
  );
}
